// This code was modified from https://www.npmjs.com/package/@mapbox-controls/ruler

import lodashForOwn from "lodash/forOwn";
import distance from "@turf/distance";
import mapboxgl from "mapbox-gl";

import store from "@/store";
import { MODULE_PREFIX_MAP } from "@/store";
import { SET_MEASURE_DISTANCE } from "@/store/mutations.type";
import { DEFAULT_LAYERS_DETAILS } from "@/common/constants/maps";

class DistanceControl {
  constructor(options = {}) {
    const defaults = {
      units: "miles",
      font: "Arial Regular",
      dark: false,
    };

    this.store = store;
    this.watcher = null;
    this.options = { ...defaults, ...options };

    this.isActive = false;
    this.coordinates = [];
    this.markers = [];

    this.lineLayer = {
      id: "mapbox-control-distance-line",
      type: "line",
      source: "mapbox-control-distance-lines",
      layout: {},
      paint: {
        "line-color": "#263238",
        "line-width": 2,
      },
    };
    this.symbolLayer = {
      id: "mapbox-control-distance-symbol",
      type: "symbol",
      source: "mapbox-control-distance-points",
      layout: {
        "text-field": ["get", "segmentFormatted"],
        "text-font": [this.options.font],
        "text-size": 10,
        "text-offset": [0, 1.25],
        "text-anchor": "top",
      },
      paint: {
        "text-color": "#263238",
        "text-halo-color": "#fff",
        "text-halo-width": 1,
      },
    };

    this.container = document.createElement("div");
    this.container.style.width = "300px";
    this.container.classList.add(
      "mapboxgl-ctrl",
      "mapboxgl-ctrl-group",
      "hyp-distance"
    );
    this.container.addEventListener("contextmenu", (e) => e.preventDefault());

    this.button = document.createElement("button");
    this.button.title = "Distance Measure Tool";
    this.button.className = "mapboxgl-ctrl-icon hyp-distance-btn";
    this.button.addEventListener("click", () => {
      if (this.isActive) {
        this.deactivate();
        this.close();
      } else {
        this.activate();
        this.open();
      }
    });

    this.infoContainer = document.createElement("div");
    this.infoContainer.className = "hyp-distance-text-container";
    this.infoContainer.style.display = "none";

    this.closeIcon = document.createElement("i");
    this.closeIcon.title = "Close";
    this.closeIcon.className =
      "fa fa-times pull-right cursor-pointer hyp-distance-close-icon";
    this.closeIcon.addEventListener("click", () => {
      this.close();
    });
    this.infoContainer.appendChild(this.closeIcon);

    this.titleContainer = document.createElement("div");
    this.titleContainer.className = "hyp-distance-title application-heading";
    this.titleContainer.innerHTML = "Distance";
    this.infoContainer.appendChild(this.titleContainer);

    this.textContainer = document.createElement("div");
    this.textContainer.className = "hyp-distance-text";
    this.infoContainer.appendChild(this.textContainer);

    this.resetLink = document.createElement("a");
    this.resetLink.addEventListener("click", () => {
      if (this.isActive) {
        this.reset();
      }
    });
    this.resetLink.innerHTML = "Reset";
    this.resetLink.style.display = "none";
    this.infoContainer.appendChild(this.resetLink);

    this.container.appendChild(this.infoContainer);
    this.container.appendChild(this.button);
  }

  draw = () => {
    if (!this.map) throw Error("map is undefined");

    this.map.addSource(this.lineLayer.source, {
      type: "geojson",
      data: this.asLine(),
    });

    this.map.addSource(this.symbolLayer.source, {
      type: "geojson",
      data: this.asPoints(),
    });

    this.map.addLayer(this.lineLayer);
    this.map.addLayer(this.symbolLayer);
  };

  open() {
    this.infoContainer.style.display = "inline-block";
    this.updateText();
    this.initialize();
  }

  close() {
    this.deactivate();
    this.infoContainer.style.display = "none";
    this.cleanup();
  }

  initialize() {
    this.watcher = this.store.watch(
      (state) => {
        return state.user.preferences.map.baseLayer_main_map;
      },
      (value) => {
        if (value) {
          this.reset();
        }
      }
    );
  }

  cleanup() {
    if (this._watcher) {
      this._watcher();
    }
  }

  reset() {
    this.updateText();
    this.deactivate();
    this.activate();
  }

  activate() {
    if (!this.map) throw Error("map is undefined");
    this.isActive = true;
    this.markers = [];
    this.coordinates = [];
    this.map.getCanvas().style.cursor = "crosshair";

    if (store) {
      const preferences = store.getters["user/preferences"];
      if (preferences?.poisDistUnits) {
        this.options.units = preferences.poisDistUnits;
      }
      if (preferences?.map?.baseLayer_main_map) {
        lodashForOwn(DEFAULT_LAYERS_DETAILS, (value, key) => {
          if (value?.style === preferences.map.baseLayer_main_map) {
            this.options.font = DEFAULT_LAYERS_DETAILS[key].font;
            this.options.dark = DEFAULT_LAYERS_DETAILS[key].dark;
          }
        });
      }
      store.commit(MODULE_PREFIX_MAP + SET_MEASURE_DISTANCE, true);
    }
    this.setLayerColors();

    this.draw();
    this.map.on("click", this.mapClickListener);
    this.map.on("style.load", this.draw);
    if (this.button) {
      this.button.classList.add("-active");
    }
  }

  deactivate() {
    if (!this.map) throw Error("map is undefined");
    this.isActive = false;
    this.map.getCanvas().style.cursor = "";
    this.map.removeLayer(this.lineLayer.id);
    this.map.removeLayer(this.symbolLayer.id);
    this.map.removeSource(this.lineLayer.source);
    this.map.removeSource(this.symbolLayer.source);
    this.markers.forEach((m) => m.remove());
    this.map.off("click", this.mapClickListener);
    this.map.off("style.load", this.draw);
    if (this.button) {
      this.button.classList.remove("-active");
    }
    if (store) {
      store.commit(MODULE_PREFIX_MAP + SET_MEASURE_DISTANCE, false);
    }
  }

  setLayerColors() {
    this.symbolLayer.paint["text-color"] = this.options.dark
      ? "#fff"
      : "#263238";
    this.symbolLayer.paint["text-halo-color"] = this.options.dark
      ? "#263238"
      : "#fff";
    this.lineLayer.paint["line-color"] = this.options.dark ? "#fff" : "#263238";
  }

  mapClickListener = (event) => {
    if (!this.map) throw Error("map is undefined");
    this.addCoordinate([event.lngLat.lng, event.lngLat.lat]);
  };

  addCoordinate(coordinate) {
    if (!this.map) throw Error("map is undefined");
    if (!this.isActive) throw Error("distance is not active");
    this.coordinates.push(coordinate);
    const markerElement = document.createElement("div");
    markerElement.style.width = "10px";
    markerElement.style.height = "10px";
    markerElement.style.borderRadius = "50%";
    markerElement.style.background = this.options.dark ? "#263238" : "#fff";
    markerElement.style.boxSizing = "border-box";
    markerElement.style.border = `2px solid ${
      this.options.dark ? "#fff" : "#263238"
    }`;
    const marker = new mapboxgl.Marker({
      element: markerElement,
      draggable: true,
    });
    marker.setLngLat(coordinate);
    marker.addTo(this.map);
    this.markers.push(marker);
    this.updateSource();
    const markerIndex = this.markers.length - 1;
    marker.on("drag", () => {
      const lngLat = marker.getLngLat();
      this.coordinates[markerIndex] = [lngLat.lng, lngLat.lat];
      this.updateSource();
    });
  }

  updateSource() {
    if (!this.map) throw Error("map is undefined");
    const lineSource = this.map.getSource(this.lineLayer.source);
    lineSource.setData(this.asLine());
    const symbolSource = this.map.getSource(this.symbolLayer.source);
    const points = this.asPoints();
    symbolSource.setData(points);
    this.updateText(points);
  }

  updateText(points) {
    this.textContainer.innerHTML = `
      <p>Select points on the map to measure distances between them. 
      Distance between individual points will be displayed on the map. 
      Total distance between all points is displayed below.</p>
    `;
    this.resetLink.style.display = "none";
    if (points?.features?.length > 1) {
      const sum =
        points.features[points.features.length - 1].properties.sumFormatted;
      this.textContainer.innerHTML += `
        <p class="application-heading">Total distance: ${sum}</p>
      `;
      this.resetLink.style.display = "inline-block";
    }
  }

  asLine() {
    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: this.coordinates,
      },
    };
  }

  asPoints() {
    let sum = 0;
    const fc = {
      type: "FeatureCollection",
      features: this.coordinates.map((coordinate, index) => {
        let segment = 0;
        if (index > 0) {
          segment = distance(this.coordinates[index - 1], coordinate, {
            units: this.options.units,
          });
          sum += segment;
        }
        return {
          type: "Feature",
          properties: {
            segment: segment,
            segmentFormatted: index > 0 ? this.labelFormat(segment) : "",
            sum: sum,
            sumFormatted: index > 0 ? this.labelFormat(sum) : "",
          },
          geometry: {
            type: "Point",
            coordinates: coordinate,
          },
        };
      }),
    };
    return fc;
  }

  // method used by mapboxgl to add this control
  onAdd(map) {
    this.map = map;
    if (this.button) {
      this.container.appendChild(this.button);
    }
    return this.container;
  }

  // method used by mapboxgl to remove this control
  onRemove() {
    if (this.isActive) this.deactivate();
    this.map?.off("click", this.mapClickListener);
    this.container.parentNode?.removeChild(this.container);
    this.cleanup();
    this.map = undefined;
  }

  labelFormat(number) {
    let val =
      number < 1 ? `${(number * 1000).toFixed()} m` : `${number.toFixed(1)} km`;

    if (this.options.units === "miles") {
      val =
        number < 1
          ? `${(number * 5280).toFixed()} ft`
          : `${number.toFixed(1)} mi`;
    }
    return val;
  }
}

export default DistanceControl;
